import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {FeaturePanelState} from 'FileStates';
import Annotation from 'molbio/annotations/backbone/Annotation';
import Annotations from 'molbio/annotations/backbone/Annotations';
import Translation from 'molbio/translations/backbone/Translation';
import Translations from 'molbio/translations/backbone/Translations';
import {getStore} from 'redux-stores/Store';
import AnnotationPanelContent from 'toolbar/annotation-panel/AnnotationPanelContent';
import {getAnnotationPanelProps} from 'toolbar/annotation-panel/AnnotationPanelUtil';
import * as FeaturePanelUIReduxActions from 'toolbar/feature-panel/FeaturePanelUIReduxActions';
import ReactPanelBaseView, {Subpanel} from 'toolbar/ReactPanelBaseView';
import {
  SEQUENCE_FEATURE_PANEL_ANNOTATIONS,
  SEQUENCE_FEATURE_PANEL_TRANSLATIONS,
} from 'toolbar/ToolbarConstants';
import TranslationPanelContent from 'toolbar/translation-panel/TranslationPanelContent';
import {getTranslationPanelProps} from 'toolbar/translation-panel/TranslationPanelUtil';

class FeaturePanelView extends ReactPanelBaseView<FeaturePanelState, Sequence> {
  annotations!: Annotations;
  translations!: Translations;

  handleSelectedChange(annotation: Annotation): void {
    getStore().dispatch(
      FeaturePanelUIReduxActions.selectAnnotation(this.model.id, {
        isCreateActive: false,
        name: annotation.name(),
        annotationType: annotation.annotationType(),
        start: annotation.start(),
        end: annotation.end(),
        color: annotation.color(),
        strand: annotation.strand(),
        notes: annotation.notes(),
        customFields: annotation.customFields(),
        shouldFocus: true,
      })
    );
  }

  update(panelState: FeaturePanelState, model: Sequence): void {
    super.update(panelState, model);

    if (this.model != null) {
      this.stopListening(this.model.get('_sequence'));
    }
    this.listenTo(this.model.get('_sequence'), 'change:circular', function (this: FeaturePanelView) {
      this.annotations.where({selected: true}).forEach((annotation) => annotation.set({selected: false}));
    });

    if (this.annotations != null) {
      this.stopListening(this.annotations);
    }
    if (this.translations != null) {
      this.stopListening(this.translations);
    }
    this.annotations = this.model.get('annotations');
    this.translations = this.model.get('translations');

    this.listenTo(this.annotations, 'change:selected', this.handleSelectedChange.bind(this));
    const selected = this.annotations.find((a) => a.selected());
    if (selected != null) {
      this.handleSelectedChange(selected);
    }

    this.updateRedesignedFeaturePanelView();
  }

  updateRedesignedFeaturePanelView(): void {
    const batchedRenderRows = this.listenToBatch(this.annotations, () => {
      this.render();
    });
    this.listenTo(this.annotations, 'change:selected', (_: Annotation, selected: boolean) => {
      if (selected) {
        this.setActiveTab(SEQUENCE_FEATURE_PANEL_ANNOTATIONS);
      }
    });
    this.listenTo(this.translations, 'change:selected', (_: Translation, selected: boolean) => {
      if (selected) {
        this.setActiveTab(SEQUENCE_FEATURE_PANEL_TRANSLATIONS);
        getStore().dispatch(FeaturePanelUIReduxActions.cancelCreateNewTranslation(this.model.id));
      }
    });
    this.listenTo(this.annotations, 'add remove change reset', () => {
      if (this.panelState.get('activeSubPanel') === SEQUENCE_FEATURE_PANEL_ANNOTATIONS) {
        if (this.annotations.inBatch()) {
          batchedRenderRows();
        } else {
          this.render();
        }
      }
    });
    this.listenTo(this.translations, 'add remove change reset', () => {
      this.updateTranslationsTab();
    });
    this.listenTo(this.model._translations, 'change', () => {
      this.updateTranslationsTab();
    });
    this.listenTo(this.model, 'change:translationGroups', () => {
      this.updateTranslationsTab();
    });
  }

  updateTranslationsTab(): void {
    if (this.panelState.get('activeSubPanel') === SEQUENCE_FEATURE_PANEL_TRANSLATIONS) {
      this.render();
    }
  }

  setActiveTab(tab: string): void {
    this.panelState.set({activeSubPanel: tab});
  }

  onTabClick(tab: string): void {
    const tabToEventName: {[key: string]: string} = {
      [SEQUENCE_FEATURE_PANEL_ANNOTATIONS]: 'annotation',
      [SEQUENCE_FEATURE_PANEL_TRANSLATIONS]: 'translation',
    };
    analytics.track(tabToEventName[tab], 'click_feature_panel_tab', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: this.model.id,
    });
    super.onTabClick(tab);
  }

  getSubpanels(): Array<Subpanel> {
    const annotationPanelProps = getAnnotationPanelProps(this.model, this.panelState);
    const translationPanelProps = getTranslationPanelProps(this.model, this.panelState);
    const subpanels = [
      {
        key: SEQUENCE_FEATURE_PANEL_ANNOTATIONS,
        title: 'Annotations',
        component: <AnnotationPanelContent {...annotationPanelProps} className={css(styles.panelContents)} />,
      },
    ];
    if (translationPanelProps != null) {
      subpanels.push({
        key: SEQUENCE_FEATURE_PANEL_TRANSLATIONS,
        title: 'Translations',
        component: (
          <TranslationPanelContent {...translationPanelProps} className={css(styles.panelContents)} />
        ),
      });
    }
    return subpanels;
  }
}

const styles = StyleSheet.create({
  panelContents: {
    overflowY: 'auto',
    // ReactPanelBaseView already has padding, but we don't want it on this panel
    margin: '0 -15px -10px',
  },
});
export default FeaturePanelView;
